import { useToast } from "@chakra-ui/react";
import { useContext, useState } from "react";
import { useIntl } from "react-intl";

import { ProjectRead } from "@nens/rana/api-v1";
import { SubmitFunction } from "@nens/ranalib";

import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { ProjectContext } from "@/stores/project-provider";

import { usePutProject } from "../api/put-project";
import { ProjectFormValues, ProjectSettingValues } from "../types/project-form-values";
import { validateProject } from "../utils/project-validation";

export function useProjectSettings() {
  const intl = useIntl();
  const toast = useToast();
  const project = useContext(ProjectContext);
  const { permissionsLoading, canWriteProject, canManageProject } = useProjectActionAllowed();

  const projectLocationFeature = project.location
    ? ({
        type: "Feature",
        geometry: project.location,
        properties: { name: project.locationName ?? null },
      } as const)
    : null;

  const INITIAL_VALUES: ProjectSettingValues = {
    name: project.name,
    code: project.code,
    id: project.id,
    description: project.description,
    location: projectLocationFeature,
  };

  const { isMutating: isPuttingProject, mutate } = usePutProject();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const cancelEdit = () => setEditMode(false);
  const startEdit = () => setEditMode(true);

  const validate = (values: ProjectFormValues) => validateProject(values, intl);

  const submit: SubmitFunction<ProjectFormValues> = (values, onError) => {
    const name = values.name + "";
    const code = values.code + "";
    const description = values.description + "";
    const location = values.location ? values.location.geometry : undefined;
    const locationName = values.location?.properties?.name ?? undefined;

    const project: Partial<ProjectRead> = {
      name,
      code,
      description,
      location,
      locationName,
    };

    mutate(project, {
      onSuccess: () => {
        setEditMode(false);
        toast({
          title: intl.formatMessage({ id: "success", defaultMessage: "Success!" }),
          description: intl.formatMessage({
            id: "projectsettings.success",
            defaultMessage: "Project settings updated successfully.",
          }),
        });
      },
      onError,
    });
  };

  return {
    initialValues: INITIAL_VALUES,
    project,
    editMode,
    isMutating: permissionsLoading || isPuttingProject,
    canWriteProject,
    canManageProject,
    showDeleteModal,
    setShowDeleteModal,
    cancelEdit,
    startEdit,
    validate,
    submit,
  };
}
